import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CarouselPreview } from '../components/carousel-preview';
import { COUNTRIES, CountryTabs } from '../components/country-tabs';
import { CoverLinePicker } from '../components/cover-line-picker';
import { PlatformChips } from '../components/platform-chip';
import { TestUserPicker } from '../components/test-user-picker';
import { ApiError } from '../config/api';
import { useRenderCarousel } from '../hooks/use-render-carousel';
import { useRuns } from '../hooks/use-runs';
import type { CardTheme, FormatId } from '../types/api';

function validCountryCode(raw: string | null): string {
  if (!raw) return 'global';
  const upper = raw.toUpperCase();
  const found = COUNTRIES.find(
    (c) => c.code.toUpperCase() === upper || c.code === raw,
  );
  return found?.code ?? 'global';
}

/**
 * The main composer screen.
 *
 * Source (left)  →  Platforms (left)  →  Render (left)
 * Preview (right) — horizontal carousel strips per platform after render
 *
 * Mirrors the design-handoff composer layout (sidebar + stage), at a smaller
 * surface area for v1 (just source + platforms; caption-template management
 * lands in Phase 4b).
 */
export function ComposerPage() {
  // Country tab state lives in the URL so refresh / bookmark / share holds
  // the selection. ?country=SE → Swedish tab; missing → global.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCountry = validCountryCode(searchParams.get('country'));

  const [testUserEmail, setTestUserEmail] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [articleCount, setArticleCount] = useState(4);
  const [coverLine, setCoverLine] = useState<string | undefined>(undefined);
  const [theme, setTheme] = useState<CardTheme | undefined>(undefined);
  const [platforms, setPlatforms] = useState<FormatId[]>([
    'ig_portrait',
  ]);

  // When the country tab changes, clear the selected test user (the new tab's
  // fixture list may not include the previously selected email) and prefill
  // the per-render country-override box with the tab code, so the render's
  // feed defaults to that country's news. Switching to the global tab clears
  // the override.
  useEffect(() => {
    setTestUserEmail('');
    setCountry(activeCountry === 'global' ? '' : activeCountry);
  }, [activeCountry]);

  function onCountryChange(code: string) {
    const next = new URLSearchParams(searchParams);
    if (code === 'global') next.delete('country');
    else next.set('country', code);
    setSearchParams(next, { replace: true });
  }

  const render = useRenderCarousel();
  const runs = useRuns(10);
  const canRender =
    Boolean(testUserEmail) && platforms.length > 0 && !render.isPending;

  function onRender() {
    render.mutate({
      testUserEmail,
      country: country || undefined,
      category: category || undefined,
      articleCount,
      coverLine: coverLine?.trim() ? coverLine.trim() : undefined,
      theme,
      platforms,
    });
  }

  return (
    <div className="composer">
      <aside className="composer-side">
        <section className="panel panel-tabs">
          <div className="panel-label">Country</div>
          <CountryTabs value={activeCountry} onChange={onCountryChange} />
        </section>

        <section className="panel">
          <div className="panel-label">Source</div>
          <label>
            Test user
            <TestUserPicker
              value={testUserEmail}
              onChange={setTestUserEmail}
              country={activeCountry === 'global' ? null : activeCountry}
            />
          </label>
          <label>
            Country override
            <input
              type="text"
              placeholder="optional · SE, IL, US…"
              value={country}
              onChange={(e) =>
                setCountry(e.target.value.toUpperCase().slice(0, 2))
              }
            />
          </label>
          <label>
            Category
            <input
              type="text"
              placeholder="optional · Technology, Politics…"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </label>
          <label>
            Articles per carousel
            <input
              type="number"
              min={3}
              max={6}
              value={articleCount}
              onChange={(e) => setArticleCount(Number(e.target.value))}
            />
          </label>
          <label>
            Cover line
            <CoverLinePicker value={coverLine} onChange={setCoverLine} />
          </label>
          <label>
            Theme
            <select
              className="select"
              value={theme ?? ''}
              onChange={(e) =>
                setTheme(
                  e.target.value === '' ? undefined : (e.target.value as CardTheme),
                )
              }
            >
              <option value="">Random</option>
              <option value="dark">Dark</option>
              <option value="light">Light</option>
            </select>
          </label>
        </section>

        <section className="panel">
          <div className="panel-label">Platforms</div>
          <PlatformChips value={platforms} onChange={setPlatforms} />
        </section>

        <button
          type="button"
          className="btn-primary btn-block"
          disabled={!canRender}
          onClick={onRender}
        >
          {render.isPending ? 'Rendering…' : 'Render carousel'}
        </button>

        {render.error ? (
          <div className="error">
            {render.error instanceof ApiError
              ? `${render.error.message} — ${JSON.stringify(render.error.body)}`
              : render.error.message}
          </div>
        ) : null}

        {render.data ? (
          <div className="run-meta">
            <div>
              Run ID:{' '}
              <code className="mono">{render.data.runId ?? '—'}</code>
            </div>
            <div>Articles: {render.data.articleIds.length}</div>
            <div>Countries: {render.data.countries.join(', ') || '—'}</div>
            <div>Theme: {render.data.theme}</div>
            {render.data.caption ? (
              <div>
                Caption:
                <pre className="caption-preview">{render.data.caption}</pre>
              </div>
            ) : null}
          </div>
        ) : null}
      </aside>

      <main className="composer-stage">
        <div className="stage-head">
          <div className="crumb">
            Composer
            <span className="crumb-sep">·</span>
            <span className="dim">
              {platforms.length} platform{platforms.length === 1 ? '' : 's'}{' '}
              · {articleCount} stories
            </span>
          </div>
        </div>
        <CarouselPreview cards={render.data?.cards ?? []} />

        <section className="runs-inline">
          <div className="runs-inline-head">
            <h2 className="runs-inline-title">Recent runs</h2>
            <Link to="/runs" className="link">
              View all →
            </Link>
          </div>
          {runs.isLoading ? <div className="muted">Loading…</div> : null}
          {runs.error ? (
            <div className="error">{runs.error.message}</div>
          ) : null}
          {runs.data && runs.data.length === 0 ? (
            <div className="muted">No runs yet — render a carousel above.</div>
          ) : null}
          {runs.data && runs.data.length > 0 ? (
            <table className="runs-table runs-table-compact">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Platforms</th>
                  <th>Articles</th>
                  <th>Trigger</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {runs.data.map((row) => (
                  <tr key={row.id}>
                    <td className="mono dim">
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td>
                      {row.test_user_email.replace('@newswave.test', '')}
                    </td>
                    <td>
                      <span className={`pill pill-${row.status}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="dim">{row.platforms.join(', ')}</td>
                    <td className="dim">{row.article_ids.length}</td>
                    <td className="dim">{row.trigger}</td>
                    <td>
                      <Link to={`/runs/${row.id}`} className="link">
                        open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      </main>
    </div>
  );
}
